import React, { useState, useEffect } from 'react';
import { getNearbyTrains, getTrainsByCrs } from '../services/api';
import { Train, StationInfo, Station, FilteredTrain, Diagnostics } from '../types';
import { useGeolocation } from '../hooks/useGeolocation';
import TrainCard from './TrainCard';
import InfoCard from './InfoCard';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Link,
  TextField,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

interface TrainDashboardProps {
  crsCode?: string;
  onChangeFavourites: () => void;
  favouriteStations: Station[];
  onStationSuggestions?: (suggestions: StationInfo[]) => void;
  timeSavedThreshold?: number;
}

const fetchTrainsData = async (
  crsCode: string | undefined,
  latitude: number | null,
  longitude: number | null,
  favouriteStations: Station[],
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<React.ReactNode | null>>,
  setStationSuggestions: React.Dispatch<React.SetStateAction<StationInfo[]>>,
  setGroupedTrains: React.Dispatch<React.SetStateAction<Record<string, Train[]>>>,
  setStationInfo: React.Dispatch<React.SetStateAction<StationInfo[] | null>>,
  setLastUpdated: React.Dispatch<React.SetStateAction<Date>>,
  setRateLimits: React.Dispatch<React.SetStateAction<Record<string, number | null>>>,
  setFilteredTrains: React.Dispatch<React.SetStateAction<FilteredTrain[]>>,
  setDiagnostics: React.Dispatch<React.SetStateAction<Diagnostics | undefined>>,
  setIsStale: React.Dispatch<React.SetStateAction<boolean>>,
  onStationSuggestions?: (suggestions: StationInfo[]) => void,
  scenarioName?: string,
  timeSavedThreshold?: number
) => {
  // If we rely on geolocation but don't have it yet, do nothing (keep loading state as is).
  if (!crsCode && (latitude === null || longitude === null)) {
    return;
  }

  setLoading(true);
  setError(null);

  try {
    if (favouriteStations.length === 0) {
      setError('No favourite stations selected.');
      // finally block will set loading to false
      return;
    }

    let trainData;
    if (crsCode) {
      trainData = await getTrainsByCrs(crsCode, favouriteStations, scenarioName, timeSavedThreshold);
    } else if (latitude !== null && longitude !== null) {
      trainData = await getNearbyTrains(latitude, longitude, favouriteStations, scenarioName, timeSavedThreshold);
    } else {
       // Should be unreachable due to top-level check
       return;
    }
    
    if (trainData.station_suggestions) {
      setStationSuggestions(trainData.station_suggestions);
      if (onStationSuggestions) {
        onStationSuggestions(trainData.station_suggestions);
      }
      setGroupedTrains({});
      setStationInfo(null);
      setRateLimits({});
      setFilteredTrains([]);
      setDiagnostics(undefined);
      setIsStale(false);
    } else {
      setStationSuggestions([]);
      if (onStationSuggestions) {
        onStationSuggestions([]);
      }
      setGroupedTrains(trainData.trains);
      setStationInfo(trainData.station_info);
      setRateLimits(trainData.rate_limits || {});
      setFilteredTrains(trainData.filtered_trains || []);
      setDiagnostics(trainData.diagnostics);
      setIsStale(Boolean(trainData.is_stale || (trainData.rate_limits && trainData.rate_limits.remaining_minute === 0)));
    }
    setLastUpdated(new Date());
  } catch (err: any) {
    let errorMessage: React.ReactNode = 'Failed to fetch train data. The backend might be offline.';
    if (err.response) {
      const data = err.response.data;
      const status = err.response.status;
      if (typeof data === 'string' && (data.trim().startsWith('<') || data.trim().toLowerCase().startsWith('<!doctype'))) {
         errorMessage = (
           <Box>
             <Typography variant="subtitle1" fontWeight="bold">Backend Error ({status})</Typography>
             <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'auto', maxHeight: '400px' }}>
                <div dangerouslySetInnerHTML={{ __html: data }} />
             </Box>
           </Box>
         );
      } else if (typeof data === 'object' && data !== null) {
        // Try to find a message field, otherwise stringify
        const msg = data.error || data.message || JSON.stringify(data);
        errorMessage = `Backend Error (${status}): ${msg}`;
      } else {
        errorMessage = `Backend Error (${status}): ${data}`;
      }
    } else if (err.message) {
      errorMessage = `Error: ${err.message}`;
    }
    setError(errorMessage);
    console.error(err);
  } finally {
    setLoading(false);
  }
};

const TrainDashboard: React.FC<TrainDashboardProps> = ({
  crsCode,
  onChangeFavourites,
  favouriteStations,
  onStationSuggestions,
  timeSavedThreshold,
}) => {
  const [groupedTrains, setGroupedTrains] = useState<Record<string, Train[]>>({});
  const [stationSuggestions, setStationSuggestions] = useState<StationInfo[]>([]);
  const [stationInfo, setStationInfo] = useState<StationInfo[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const { latitude, longitude, error: geoError } = useGeolocation(!crsCode);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  
  // Debug & rate limiting info
  const [rateLimits, setRateLimits] = useState<Record<string, number | null>>({});
  const [filteredTrains, setFilteredTrains] = useState<FilteredTrain[]>([]);
  const [diagnostics, setDiagnostics] = useState<Diagnostics | undefined>(undefined);
  const [isStale, setIsStale] = useState<boolean>(false);
  
  // Dev tools state
  const [scenarioName, setScenarioName] = useState('');
  const [copied, setCopied] = useState(false);

  const targetScenarioName = scenarioName.trim() || 'my-scenario';
  const favCodes = favouriteStations.map(s => s.crsCode).join(',');
  let captureCommand = '';
  if (crsCode) {
    captureCommand = `python manage_scenarios.py capture ${targetScenarioName} --from-crs ${crsCode} --favourites ${favCodes}`;
  } else if (latitude !== null && longitude !== null) {
    captureCommand = `python manage_scenarios.py capture ${targetScenarioName} --lat ${latitude.toFixed(4)} --lon ${longitude.toFixed(4)} --favourites ${favCodes}`;
  } else {
    captureCommand = `python manage_scenarios.py capture ${targetScenarioName} --favourites ${favCodes}`;
  }

  const handleCopyCommand = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(captureCommand);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy command:', err);
    }
  };

  useEffect(() => {
    const triggerFetch = () => {
      if (document.hidden) {
        return;
      }
      fetchTrainsData(
        crsCode,
        latitude,
        longitude,
        favouriteStations,
        setLoading,
        setError,
        setStationSuggestions,
        setGroupedTrains,
        setStationInfo,
        setLastUpdated,
        setRateLimits,
        setFilteredTrains,
        setDiagnostics,
        setIsStale,
        onStationSuggestions,
        undefined,
        timeSavedThreshold
      );
    };

    triggerFetch();
    const interval = setInterval(triggerFetch, 30000);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        triggerFetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [crsCode, latitude, longitude, favouriteStations, onStationSuggestions, timeSavedThreshold]);

  const handleCapture = () => {
    if (!scenarioName) {
      alert("Please enter a scenario name");
      return;
    }
    fetchTrainsData(
      crsCode,
      latitude,
      longitude,
      favouriteStations,
      setLoading,
      setError,
      setStationSuggestions,
      setGroupedTrains,
      setStationInfo,
      setLastUpdated,
      setRateLimits,
      setFilteredTrains,
      setDiagnostics,
      setIsStale,
      onStationSuggestions, // Pass the new prop
      scenarioName,
      timeSavedThreshold
    );
  };
  
  if (geoError && !crsCode) {
    return (
      <Container maxWidth="md">
        <Alert severity="warning" sx={{ mt: 4 }}>
          <Typography>
            Geolocation is not available. You can select one of your favourite stations to view departures:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {favouriteStations.map((station) => (
              <Typography key={station.crsCode}>
                <a href={`/from/${station.crsCode}`}>
                  {station.stationName}
                </a>
              </Typography>
            ))}
          </Box>
        </Alert>
      </Container>
    );
  }

  if (loading && Object.keys(groupedTrains).length === 0) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {crsCode ? `Loading trains from ${crsCode}...` : 'Getting your location...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  const hasTrains = Object.keys(groupedTrains).some(destination => groupedTrains[destination].length > 0);
  const allTrains = Object.values(groupedTrains).flat();
  const distinctTrainOrigins = new Set(allTrains.map((t) => t.from_crs).filter(Boolean));
  const hasMultipleOrigins = !crsCode && Boolean(
    (stationInfo && stationInfo.length > 1) || distinctTrainOrigins.size > 1
  );

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ mb: 2, pr: { xs: 6, sm: 0 } }}>
          <Typography variant="h4" component="h1">
            {crsCode ? `Departures from ${crsCode}` : 'Nearby Departures'}
          </Typography>
          <InfoCard
            lastUpdated={lastUpdated}
            onChangeFavourites={onChangeFavourites}
            stationInfo={stationInfo}
            crsCode={crsCode}
            isStale={isStale}
          />
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && !hasTrains && stationSuggestions.length === 0 && (
          <Alert severity={isStale ? "warning" : "info"}>
            {isStale
              ? "Live updates paused (rate limit reached). No departures currently cached."
              : "No trains found from your nearby stations to your favourite destinations in the next hour."}
          </Alert>
        )}

        {stationSuggestions.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography>
              No trains found from your current nearby stations to your favourite destinations.
              You could try departing from one of the following stations instead:
            </Typography>
            <Box sx={{ mt: 2 }}>
              {stationSuggestions.map((station) => (
                <Typography key={station.crs}>
                  <a href={`/from/${station.crs}`}>
                    {station.name}
                  </a>
                  {' '}
                  ({station.distance_miles} miles away)
                </Typography>
              ))}
            </Box>
          </Alert>
        )}

        {Object.keys(groupedTrains).map(destination => {
          const trainsForDestination = groupedTrains[destination];
          if (trainsForDestination.length === 0) return null;
          return (
            <Box key={destination} sx={{ mb: 4 }}>
              <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                To {destination}
              </Typography>
              {trainsForDestination.map((train) => (
                <TrainCard
                  key={train.service_uid}
                  train={train}
                  destination={destination}
                  showOriginCrs={hasMultipleOrigins}
                />
              ))}
            </Box>
          );
        })}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="body2">
            Data provided by <Link href="https://www.realtimetrains.co.uk/">Realtime Trains</Link>
          </Typography>
        </Box>

        <Accordion sx={{ mt: 4 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">API Rate Limits, Diagnostics & Filtered Trains</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">RTT Next Gen API Rate Limits:</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mt: 1 }}>
                <Box>
                  <Typography variant="body2"><strong>Minute:</strong> {rateLimits.remaining_minute ?? 'N/A'} / {rateLimits.limit_minute ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2"><strong>Hour:</strong> {rateLimits.remaining_hour ?? 'N/A'} / {rateLimits.limit_hour ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2"><strong>Day:</strong> {rateLimits.remaining_day ?? 'N/A'} / {rateLimits.limit_day ?? 'N/A'}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2"><strong>Week:</strong> {rateLimits.remaining_week ?? 'N/A'} / {rateLimits.limit_week ?? 'N/A'}</Typography>
                </Box>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Backend Diagnostics & Errors:</Typography>
              {diagnostics && ((diagnostics.darwin_errors && diagnostics.darwin_errors.length > 0) || (diagnostics.rtt_errors && diagnostics.rtt_errors.length > 0)) ? (
                <Box sx={{ mt: 1 }}>
                  {diagnostics.darwin_errors && diagnostics.darwin_errors.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary">Darwin:</Typography>
                      {diagnostics.darwin_errors.map((err, idx) => (
                        <Typography key={`darwin-err-${idx}`} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', pl: 1, py: 0.25 }}>
                          • {err}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {diagnostics.rtt_errors && diagnostics.rtt_errors.length > 0 && (
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="text.secondary">Realtime Trains:</Typography>
                      {diagnostics.rtt_errors.map((err, idx) => (
                        <Typography key={`rtt-err-${idx}`} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', pl: 1, py: 0.25 }}>
                          • {err}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>No backend errors reported.</Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Filtered Out Departures:</Typography>
              {filteredTrains.length === 0 ? (
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>No trains filtered out.</Typography>
              ) : (
                <Box sx={{ mt: 1, maxHeight: '200px', overflowY: 'auto' }}>
                  {filteredTrains.map((train, idx) => (
                    <Typography key={`${train.service_uid}-${idx}`} variant="body2" sx={{ fontFamily: 'monospace', py: 0.5 }}>
                      • {train.text}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Developer Tools (Capture Scenario):</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                Simulate and capture this exact request into a local test scenario using the CLI:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField 
                  label="Scenario Name" 
                  size="small" 
                  placeholder="my-scenario"
                  value={scenarioName} 
                  onChange={(e) => setScenarioName(e.target.value)} 
                  sx={{ minWidth: 200 }}
                />
                <Button 
                  variant="contained" 
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  color={copied ? "success" : "primary"}
                  onClick={handleCopyCommand}
                >
                  {copied ? "Copied Command!" : "Copy Command"}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleCapture}
                >
                  Capture (Local Server)
                </Button>
              </Box>
              <Box 
                sx={{ 
                  p: 1.5, 
                  bgcolor: 'action.hover', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1
                }}
              >
                <Typography component="code" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}>
                  {captureCommand}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                  <IconButton size="small" onClick={handleCopyCommand} aria-label="Copy command to clipboard">
                    {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
};

export default TrainDashboard;
